// status-supervisor-liveness.ts — 06k C10 (D6, B13/N14): supervisor sống = pid
// trong machine lock CỦA mission này còn sống và mốc khởi động khớp (pid tái dùng
// ⇒ chết). `status --wait`: chỉ đọc lock + `last-run.json`, không giành gì.
import { type MachineLockFile, readMachineLock } from "./mission-lock-file-io.js";
import {
  deadOrPidReused,
  type LockLivenessProbe,
  realLivenessProbe,
} from "./process-liveness-probe.js";
import { readLastRun } from "./run-last-outcome-file.js";

export const STATUS_WAIT_MAX_SEC = 540;
const DEFAULT_WAIT_POLL_MS = 5_000;

export interface SupervisorLiveness {
  supervisor_alive: boolean;
  supervisor_pid?: number;
  /** Lock đang thuộc mission KHÁC (máy chỉ chạy một supervisor). */
  machine_lock_mission_id?: string;
}

async function livenessOf(
  lock: MachineLockFile | undefined,
  missionId: string,
  probe: LockLivenessProbe,
): Promise<SupervisorLiveness> {
  if (lock === undefined) return { supervisor_alive: false };
  if (lock.missionId !== missionId) {
    return { supervisor_alive: false, machine_lock_mission_id: lock.missionId };
  }
  if (await deadOrPidReused(probe, lock.pid, lock.processStartedAt)) {
    return { supervisor_alive: false };
  }
  return { supervisor_alive: true, supervisor_pid: lock.pid };
}

export async function readSupervisorLiveness(
  machineLockPath: string,
  missionId: string,
  probe: LockLivenessProbe = realLivenessProbe,
): Promise<SupervisorLiveness> {
  return livenessOf(await readMachineLock(machineLockPath), missionId, probe);
}

/** Everything the progress panel needs from ONE read of the machine lock (no race between reads). */
export interface MachineLockFacts {
  liveness: SupervisorLiveness;
  /** The lock on disk names THIS mission (orphaned when `liveness.supervisor_alive` is false). */
  ownLockPresent: boolean;
  /** The lock names ANOTHER mission whose pid is alive (not dead, not reused) — the machine is really busy. */
  otherLockAlive: boolean;
}

export async function readMachineLockFacts(
  machineLockPath: string,
  missionId: string,
  probe: LockLivenessProbe = realLivenessProbe,
): Promise<MachineLockFacts> {
  const lock = await readMachineLock(machineLockPath);
  const other = lock !== undefined && lock.missionId !== missionId;
  return {
    liveness: await livenessOf(lock, missionId, probe),
    ownLockPresent: lock?.missionId === missionId,
    otherLockAlive: other && !(await deadOrPidReused(probe, lock.pid, lock.processStartedAt)),
  };
}

export interface EngineWatchInput {
  machineLockPath: string;
  controlRoot: string;
  missionId: string;
}

/** Engine còn chạy: supervisor sống, HOẶC last_run "running" mà pid còn sống, không tái dùng (N14, L6). */
async function engineActive(input: EngineWatchInput): Promise<boolean> {
  const live = await readSupervisorLiveness(input.machineLockPath, input.missionId);
  if (live.supervisor_alive) return true;
  const last = await readLastRun(input.controlRoot);
  if (last?.state !== "running") return false;
  return !(await deadOrPidReused(realLivenessProbe, last.pid, last.process_started_at));
}

/**
 * `--wait <N>`: đang chạy lúc bắt đầu ⇒ chặn tới khi dừng; đã dừng ⇒ ngủ đủ N s,
 * trả sớm nếu supervisor sống lại. Trả `true` khi hết N s (wait_timed_out).
 */
export async function waitForEngineChange(
  input: EngineWatchInput & { waitSec: number; pollMs?: number },
): Promise<boolean> {
  const deadline = Date.now() + input.waitSec * 1000;
  const pollMs = input.pollMs ?? DEFAULT_WAIT_POLL_MS;
  const activeAtStart = await engineActive(input);
  for (;;) {
    const left = deadline - Date.now();
    if (left <= 0) return true;
    await new Promise((r) => setTimeout(r, Math.min(pollMs, left)));
    if (activeAtStart && !(await engineActive(input))) return false;
    if (
      !activeAtStart &&
      (await readSupervisorLiveness(input.machineLockPath, input.missionId)).supervisor_alive
    ) {
      return false;
    }
  }
}
